import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, 
  ChevronRight, 
  Clock, 
  AlertTriangle,
  CheckCircle,
  Activity
} from 'lucide-react';
import { motion } from 'framer-motion';

interface Project {
  id: string;
  name: string;
  client: string;
  status: string;
  progress: number;
}

interface Task {
  id: string;
  name: string;
  status: string;
  end_date: string;
  progress: number;
  project_name: string;
}

interface QuickGlanceWidgetsProps {
  recentProjects: Project[];
  activeTasks: Task[];
  isLoaded: boolean;
}

const statusConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  'em_andamento': { label: 'Andamento', color: 'text-blue-600', bgColor: 'bg-blue-500/10' },
  'planejamento': { label: 'Planejando', color: 'text-slate-600', bgColor: 'bg-slate-500/10' },
  'atrasado': { label: 'Atrasado', color: 'text-red-600', bgColor: 'bg-red-500/10' },
  'concluido': { label: 'Concluído', color: 'text-emerald-600', bgColor: 'bg-emerald-500/10' },
  'pausado': { label: 'Pausado', color: 'text-amber-600', bgColor: 'bg-amber-500/10' },
};

export const QuickGlanceWidgets = ({ recentProjects, activeTasks, isLoaded }: QuickGlanceWidgetsProps) => {
  const navigate = useNavigate();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Recent Projects */}
      <Card className="bento-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              Projetos Recentes
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 text-xs gap-1 text-muted-foreground hover:text-foreground"
              onClick={() => navigate('/projects')}
            >
              Ver todos
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {recentProjects.length > 0 ? (
            recentProjects.slice(0, 3).map((project, index) => {
              const status = statusConfig[project.status] || { label: project.status, color: 'text-slate-600', bgColor: 'bg-slate-500/10' };
              
              return (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group flex items-center gap-4 p-3 rounded-xl bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/projetos/${project.id}`)}
                >
                  <div className={`w-10 h-10 rounded-lg ${status.bgColor} flex items-center justify-center flex-shrink-0`}>
                    <Building2 className={`h-5 w-5 ${status.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate group-hover:text-primary transition-colors">
                      {project.name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{project.client}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <Badge variant="secondary" className={`text-[10px] ${status.color} ${status.bgColor} border-0`}>
                      {status.label}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">{project.progress || 0}%</p>
                  </div>
                </motion.div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <Building2 className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">Nenhum projeto ainda</p>
              <Button 
                variant="link" 
                size="sm" 
                className="mt-2"
                onClick={() => navigate('/projects?create=true')}
              >
                Criar primeiro projeto
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Critical Tasks */}
      <Card className="bento-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" />
              Tarefas Urgentes
            </CardTitle>
            <Badge variant="secondary" className="text-xs">
              {activeTasks.length} {activeTasks.length === 1 ? 'tarefa' : 'tarefas'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {activeTasks.length > 0 ? (
            activeTasks.slice(0, 3).map((task, index) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const endDate = task.end_date ? new Date(task.end_date) : null;
              endDate?.setHours(0, 0, 0, 0);
              const isOverdue = endDate && endDate < today;
              const daysUntilDue = endDate ? Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)) : null;
              const isUrgent = daysUntilDue !== null && daysUntilDue >= 0 && daysUntilDue <= 3;

              const getStatusDisplay = () => {
                if (isOverdue) {
                  return { 
                    icon: AlertTriangle, 
                    label: `${Math.abs(daysUntilDue || 0)}d atrasada`, 
                    color: 'text-red-600',
                    bgColor: 'bg-red-500/10'
                  };
                }
                if (isUrgent) {
                  return { 
                    icon: Clock, 
                    label: daysUntilDue === 0 ? 'Hoje' : `${daysUntilDue}d restantes`, 
                    color: 'text-amber-600',
                    bgColor: 'bg-amber-500/10'
                  };
                }
                return { 
                  icon: CheckCircle, 
                  label: 'Em andamento', 
                  color: 'text-blue-600',
                  bgColor: 'bg-blue-500/10'
                };
              };

              const statusDisplay = getStatusDisplay();

              return (
                <motion.div
                  key={task.id}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-3 rounded-xl bg-muted/30"
                >
                  <div className={`w-10 h-10 rounded-lg ${statusDisplay.bgColor} flex items-center justify-center flex-shrink-0`}>
                    <statusDisplay.icon className={`h-5 w-5 ${statusDisplay.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{task.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{task.project_name}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <Badge variant="secondary" className={`text-[10px] ${statusDisplay.color} ${statusDisplay.bgColor} border-0`}>
                      {statusDisplay.label}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">{task.progress || 0}%</p>
                  </div>
                </motion.div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="h-10 w-10 mx-auto text-emerald-500/30 mb-3" />
              <p className="text-sm text-muted-foreground">Nenhuma tarefa urgente</p>
              <p className="text-xs text-muted-foreground/70 mt-1">Ótimo trabalho!</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
